import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { client as clientSchemas, insights } from "@duress/shared";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api-client";
import { updateClientInfo } from "./api";
import { useCreateClient } from "./useClients";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateClientDialog({ open, onOpenChange }: Props): React.JSX.Element {
  const [name, setName] = useState("");
  const [industries, setIndustries] = useState<string[]>([]);
  const [region, setRegion] = useState<clientSchemas.Region>("AU");
  // Left unset (null) rather than defaulting to false -- most clients do
  // have devices, but forcing an explicit choice here is what lets the new
  // client land on the right tab immediately instead of a checkbox buried
  // in Details -> Setup after the fact.
  const [appOnly, setAppOnly] = useState<boolean | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const create = useCreateClient();
  const navigate = useNavigate();

  const toggleIndustry = (industry: string): void => {
    setIndustries((prev) =>
      prev.includes(industry) ? prev.filter((i) => i !== industry) : [...prev, industry],
    );
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const created = await create.mutateAsync({ name: trimmed, industries, region });
      // A freshly created client has every optional field at its default
      // (null), so it's safe to send those straight back -- updateClientInfo
      // is a full-replace PATCH, not a partial merge.
      if (appOnly !== null) {
        await updateClientInfo(created.id, {
          region: created.region,
          pathfinderUsername: created.pathfinderUsername,
          googleDriveUrl: created.googleDriveUrl,
          canvaUrl: created.canvaUrl,
          contractEndDate: created.contractEndDate,
          falconCount: created.falconCount,
          eagleCount: created.eagleCount,
          appOnly,
          tablets: created.tablets,
          simProvider: created.simProvider,
          customEscalation: created.customEscalation,
          receivesMonthlyReports: created.receivesMonthlyReports,
          arr: created.arr,
        });
      }
      setName("");
      setIndustries([]);
      setAppOnly(null);
      setRegion("AU");
      onOpenChange(false);
      // App Only clients have no device CSVs to upload -- send them straight
      // to App Usage instead of popping the (device-oriented) period upload
      // dialog they'd just have to close.
      if (appOnly === true) {
        navigate(`/clients/${created.slug}?tab=app-usage`);
      } else {
        navigate(`/clients/${created.slug}`, { state: { openUpload: true } });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError(
          "A client with that name (or a very similar one) already exists. Try a slightly different name.",
        );
      } else {
        setSubmitError(
          err instanceof Error ? err.message : "Couldn't create that client. Try again.",
        );
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setName("");
          setIndustries([]);
          setAppOnly(null);
          setRegion("AU");
          setSubmitError(null);
        }
        onOpenChange(o);
      }}
    >
      <DialogHeader>
        <DialogTitle>New client</DialogTitle>
        <DialogDescription>
          The slug is generated from the name automatically. You can edit the tier-scoring
          config after creating.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="client-name">Client name</Label>
          <Input
            id="client-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Coffee"
            autoFocus
            required
          />
        </div>

        <div className="space-y-2">
          <Label className="text-sm">Region</Label>
          <div className="flex gap-2">
            {clientSchemas.REGIONS.map((r) => (
              <Button
                key={r}
                type="button"
                variant={region === r ? "default" : "outline"}
                size="sm"
                onClick={() => setRegion(r)}
              >
                {r}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">Industries <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <div className="grid grid-cols-2 gap-2">
            {insights.INDUSTRIES.map((industry) => {
              const checked = industries.includes(industry);
              return (
                <label
                  key={industry}
                  className="flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm transition-colors hover:bg-accent"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleIndustry(industry)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span>{industry}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">
            App Only? <span className="text-muted-foreground font-normal">(no Falcon/Eagle devices — optional, can change later)</span>
          </Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={appOnly === true ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setAppOnly(appOnly === true ? null : true);
              }}
            >
              Yes
            </Button>
            <Button
              type="button"
              variant={appOnly === false ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setAppOnly(appOnly === false ? null : false);
              }}
            >
              No
            </Button>
          </div>
        </div>

        {submitError ? (
          <p className="text-sm text-destructive">{submitError}</p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            {create.isPending ? "Creating…" : "Create client"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
